import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styles from './SidebarChatMenu.module.css';

type MenuItem = {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  danger?: boolean;
};

type SidebarChatMenuProps = {
  onRename: () => void;
  onDelete: () => void;
  onPinChat?: () => void;
  isPinned?: boolean;
};


export const SidebarChatMenu: React.FC<SidebarChatMenuProps> = ({
  onRename,
  onDelete,
  onPinChat,
  isPinned = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inDropdown = menuRef.current?.contains(target);
      const inButton = buttonRef.current?.contains(target);
      if (!inDropdown && !inButton) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 180;
      const left = Math.max(4, rect.right - dropdownWidth);
      setDropdownPos({ top: rect.bottom + 4, left });
    }
    setIsOpen(!isOpen);
  };

  const menuItems: MenuItem[] = [
    {
      label: isPinned ? 'Unpin chat' : 'Pin chat',
      icon: <PinIcon isPinned={isPinned} />,
      onClick: () => { onPinChat?.(); setIsOpen(false); },
    },
    {
      label: 'Rename',
      icon: <RenameIcon />,
      onClick: () => { onRename(); setIsOpen(false); },
    },
    {
      label: 'Delete',
      icon: <DeleteIcon />,
      onClick: () => { onDelete(); setIsOpen(false); },
      danger: true,
    },
  ];

  const dropdown = isOpen ? (
    <div
      className={styles.menuDropdown}
      style={{ top: dropdownPos.top, left: dropdownPos.left }}
      ref={menuRef}
    >
      {menuItems.map((item, index) => (
        <button
          key={index}
          className={`${styles.menuItem} ${item.danger ? styles.danger : ''}`}
          onClick={(e) => { e.stopPropagation(); item.onClick(); }}
        >
          <span className={styles.menuIcon}>{item.icon}</span>
          <span>{item.label}</span>
        </button>
      ))}
    </div>
  ) : null;

  return (
    <div className={styles.menuContainer}>
      <button
        ref={buttonRef}
        className={styles.menuButton}
        onClick={handleOpen}
        aria-label="Chat options"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <DotsIcon />
      </button>
      {ReactDOM.createPortal(dropdown, document.body)}
    </div>
  );
};

// SVG Icons
const DotsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5 12H5.01M12 12H12.01M19 12H19.01M6 12C6 12.5523 5.55228 13 5 13C4.44772 13 4 12.5523 4 12C4 11.4477 4.44772 11 5 11C5.55228 11 6 11.4477 6 12ZM13 12C13 12.5523 12.5523 13 12 13C11.4477 13 11 12.5523 11 12C11 11.4477 11.4477 11 12 11C12.5523 11 13 11.4477 13 12ZM20 12C20 12.5523 19.5523 13 19 13C18.4477 13 18 12.5523 18 12C18 11.4477 18.4477 11 19 11C19.5523 11 20 11.4477 20 12Z" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

const RenameIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 13.3333H14M11 2.33333C11.1768 2.11687 11.4053 1.95219 11.6611 1.85683C11.9169 1.76147 12.191 1.73892 12.4577 1.79157C12.7244 1.84422 12.9736 1.97016 13.1766 2.15529C13.3796 2.34041 13.5288 2.57767 13.6078 2.84027C13.6868 3.10286 13.6926 3.3808 13.6246 3.64606C13.5565 3.91132 13.4171 4.15375 13.222 4.34533L7.33268 10.6667L4 11.3333L4.66668 8.5L10.556 2.17867C10.6884 2.0377 10.8416 1.91859 11.0099 1.82595L11 2.33333Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const DeleteIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 4H3.33333H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12.6663 4V13.3333C12.6663 13.687 12.5259 14.0261 12.2758 14.2761C12.0258 14.5262 11.6867 14.6667 11.333 14.6667H4.66634C4.31272 14.6667 3.97358 14.5262 3.72353 14.2761C3.47348 14.0261 3.33301 13.687 3.33301 13.3333V4M5.33301 4V2.66667C5.33301 2.31304 5.47348 1.9739 5.72353 1.72385C5.97358 1.4738 6.31272 1.33333 6.66634 1.33333H9.33301C9.68663 1.33333 10.0258 1.4738 10.2758 1.72385C10.5259 1.9739 10.6663 2.31304 10.6663 2.66667V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M6.66699 7.33333V11.3333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M9.33301 7.33333V11.3333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const PinIcon = ({ isPinned = false }: { isPinned?: boolean }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill={isPinned ? "currentColor" : "none"} xmlns="http://www.w3.org/2000/svg">
    <path 
      d="M8 1.33333V8M8 8L5.33333 4M8 8L10.6667 4M3.33333 8.66667V12.6667C3.33333 13.0203 3.47381 13.3594 3.72386 13.6095C3.97391 13.8595 4.31304 14 4.66667 14H11.3333C11.687 14 12.0261 13.8595 12.2761 13.6095C12.5262 13.3594 12.6667 13.0203 12.6667 12.6667V8.66667" 
      stroke="currentColor" 
      strokeWidth="1.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);
